import React, { useState, useRef } from 'react';
import { imageService } from '../../services/api';

interface LogoUploadProps {
  onImageUploaded: (url: string) => void;
  currentImageUrl?: string;
  className?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  onImageUploaded,
  currentImageUrl,
  className = ""
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string>(currentImageUrl || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sincronizar previewUrl com currentImageUrl quando ela mudar externamente
  React.useEffect(() => {
    setPreviewUrl(currentImageUrl || '');
  }, [currentImageUrl]);

  const compressLogo = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        // Logo deve ser quadrado, 512x512 no máximo
        const size = Math.min(img.width, img.height);
        const targetSize = Math.min(512, size);
        
        canvas.width = targetSize;
        canvas.height = targetSize;

        // Centralizar a imagem no canvas se não for quadrada
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;

        ctx.drawImage(img, sx, sy, size, size, 0, 0, targetSize, targetSize);

        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', 0.9);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const validateLogoFile = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Por favor, selecione apenas arquivos de imagem';
    }

    if (file.size > 5 * 1024 * 1024) {
      return 'A imagem deve ter no máximo 5MB';
    }

    return null;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');

    // Validações específicas para logo
    const validationError = validateLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsUploading(true);
      
      // Comprimir logo com configurações específicas
      const compressedFile = await compressLogo(file);
      
      // Mostrar preview
      const previewUrl = URL.createObjectURL(compressedFile);
      setPreviewUrl(previewUrl);

      // Upload para blob storage
      const formData = new FormData();
      formData.append('file', compressedFile);
      const response = await imageService.uploadImage(formData);
      onImageUploaded(response.url);

    } catch (err: any) {
      console.error('Erro no upload do logo:', err);
      setError(err.message || 'Erro ao fazer upload do logo');
      // Resetar para o estado anterior em caso de erro
      setPreviewUrl(currentImageUrl || '');
    } finally {
      setIsUploading(false);
    }
  };

  const removeLogo = () => {
    setPreviewUrl('');
    setError('');
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
          {error}
        </div>
      )}

      {previewUrl ? (
        <div className="relative inline-block">
          <img 
            src={previewUrl} 
            alt="Logo Preview" 
            className="w-32 h-32 object-cover rounded-lg border border-gray-300"
          />
          <button
            type="button"
            onClick={removeLogo}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
            disabled={isUploading}
          >
            ×
          </button>
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="logo-upload"
            disabled={isUploading}
          />
          <label htmlFor="logo-upload" className="cursor-pointer">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path 
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
                strokeWidth={2} 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              {isUploading ? 'Fazendo upload do logo...' : 'Clique para fazer upload do logo'}
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG até 5MB • Ideal: formato quadrado 512x512px
            </p>
          </label>
        </div>
      )}
    </div>
  );
};

export default LogoUpload;
